import { useMutation } from '@apollo/client'
import { ROUTES } from '@app/constants'
import { FollowChannelByLinkNameDocument, FollowUserByLinkNameDocument, UserRoles } from '@app/graphql'
import { AuthHelper } from '@app/helpers'
import { CurrentUserState, PrivateChannelState, PublicChannelState, UserAuthState } from '@app/storage'
import { useCallback, useState } from 'react'
import posthog from 'posthog-js'

export const useFollowLinks = () => {
  const [ followChannelByLinkName ] = useMutation(FollowChannelByLinkNameDocument)
  const [ followUserByLinkName ] = useMutation(FollowUserByLinkNameDocument)
  const [loading, setLoading] = useState(false)

  const followChannelByLink = useCallback(async () => {
    const { affiliateLinkName, inviteLinkName } = AuthHelper.getSearchParams()
    const linkName =
      affiliateLinkName ||
      UserAuthState.affiliateLinkName ||
      inviteLinkName ||
      UserAuthState.inviteLinkName

    try {
      if (!linkName) return

      if (CurrentUserState.userType === UserRoles.Tipster) {
        PublicChannelState.adminJoinDisabled = true
        return
      }

      setLoading(true)
      const { data } = await followChannelByLinkName({ variables: { internalName: linkName } })
      const channelId = data?.followChannelByLinkName?.channelId

      if (channelId) {
        PrivateChannelState.newChannelId = channelId
        localStorage.setItem('newChannelId', channelId)
        localStorage.setItem('currentChannelId', channelId)
        window.location.href = `${window.location.origin}${ROUTES.channel.short}`
      }
    } catch (err) {
      posthog.captureException(err, { feature: 'invite_link', step: 'follow_channel', link_name: linkName ?? null })
      throw err
    } finally {
      setLoading(false)
    }
  }, [followChannelByLinkName])

  const followTipsterByLink = useCallback(async () => {
    const { followLinkName } = AuthHelper.getSearchParams()
    const linkName = followLinkName || UserAuthState.followLinkName

    try {
      if (!linkName) return

      setLoading(true)
      const { data } = await followUserByLinkName({ variables: { linkName } })
      const tipsterId = data?.followUserByLinkName?.id

      if (tipsterId) {
        window.location.href = `${window.location.origin}${ROUTES.profile.short}/${tipsterId}`
      }
    } catch (err) {
      posthog.captureException(err, { feature: 'invite_link', step: 'follow_tipster', link_name: linkName ?? null })
      throw err
    } finally {
      setLoading(false)
    }
  }, [followUserByLinkName])

  const redeemVipInvite = useCallback(() => {
    const { vipInviteToken } = AuthHelper.getSearchParams()
    const token = vipInviteToken || localStorage.getItem('vipInviteToken')
    if (!token) return
    if (!CurrentUserState.id) return
    if (CurrentUserState.userType === UserRoles.Tipster) return
    if (window.location.pathname === ROUTES.tipsterSubscriberList.short) return
    // Don't interrupt onboarding — LoginShowtime already handles the VIP invite
    // redirect after the user has fully completed the onboarding flow.
    if (!CurrentUserState.hasOnboarded) return
    localStorage.removeItem('vipInviteToken')
    window.location.href = `${window.location.origin}${ROUTES.tipsterSubscriberList.short}?vipInvite=${token}`
  }, [])

  return { followChannelByLink, followTipsterByLink, redeemVipInvite, loading }
}
